// frontend/src/components/PaymentForm.tsx

import React, { useState, useEffect, FormEvent } from 'react';
import { CreatePaymentRequest } from '../types/payment';
import { getStudents } from '../api/students';
import type { Enrollment } from '../types/student';
import styles from '../styles/PaymentForm.module.css';

interface EnrollmentOption {
    studentId: string;
    studentName: string;
    studentEmail: string;
    courseId: string;
    courseName: string;
    remaining: number;
}

interface Props {
    onSubmit: (data: CreatePaymentRequest) => Promise<void>;
    onCancel: () => void;
    initialStudentId?: string;
    initialCourseId?: string;
    initialAmount?: number;
}

export default function PaymentForm({
                                        onSubmit,
                                        onCancel,
                                        initialStudentId,
                                        initialCourseId,
                                        initialAmount
                                    }: Props) {
    const [options, setOptions] = useState<EnrollmentOption[]>([]);
    const [initialOption, setInitialOption] = useState<EnrollmentOption | null>(null);
    const [selectedIdx, setSelected] = useState<number>(-1);
    const [due, setDue] = useState<number>(0);
    const [error, setError] = useState<string>('');

    const [form, setForm] = useState<Omit<CreatePaymentRequest, 'amount' | 'discountAmount'> & { amount: string; discountAmount: string }>({
        student: '',
        course: '',
        paymentDate: new Date().toISOString().slice(0, 10),
        receiptNumber: '',
        amount: '',
        discountAmount: '',
        discountReason: ''
    });

    useEffect(() => {
        (async () => {
            const students = await getStudents();
            const opts: EnrollmentOption[] = [];

            students.forEach(s => {
                s.enrollments
                    .filter((en: Enrollment) => en.remaining > 0)
                    .forEach((en: Enrollment) => {
                        opts.push({
                            studentId: s.id,
                            studentName: s.name,
                            studentEmail: s.email!,
                            courseId: en.course.id,
                            courseName: en.course.name,
                            remaining: en.remaining
                        });
                    });
            });

            setOptions(opts);

            if (initialStudentId && initialCourseId) {
                const idx = opts.findIndex(o =>
                    o.studentId === initialStudentId && o.courseId === initialCourseId
                );
                if (idx >= 0) {
                    const opt = opts[idx];
                    setInitialOption(opt);
                    setSelected(idx);
                    setDue(opt.remaining);
                    setForm(prev => ({
                        ...prev,
                        student: opt.studentId,
                        course: opt.courseId,
                        amount: initialAmount != null
                            ? String(Math.min(initialAmount, opt.remaining))
                            : ''
                    }));
                }
            }
        })();
    }, [initialStudentId, initialCourseId, initialAmount]);

    const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const idx = Number(e.target.value);
        setSelected(idx);

        if (idx >= 0 && options[idx]) {
            const opt = options[idx];
            setDue(opt.remaining);
            setForm(prev => ({
                ...prev,
                student: opt.studentId,
                course: opt.courseId,
                amount: prev.amount ? String(Math.min(Number(prev.amount), opt.remaining)) : ''
            }));
        } else {
            setDue(0);
            setForm(prev => ({ ...prev, student: '', course: '', amount: '' }));
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (name === 'amount' || name === 'discountAmount') {
            if (/^\d*$/.test(value)) {
                setForm(prev => ({ ...prev, [name]: value }));
            }
        } else {
            setForm(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');

        const numericAmount = Number(form.amount || 0);
        const numericDiscount = Number(form.discountAmount || 0);

        if (isNaN(numericAmount) || isNaN(numericDiscount) || numericAmount < 0 || numericDiscount < 0) {
            setError('Please enter valid payment and discount amounts');
            return;
        }
        if (numericAmount === 0 && numericDiscount === 0) {
            setError('Enter a payment amount, a discount, or both');
            return;
        }
        if (numericAmount + numericDiscount > due) {
            setError(`Payment and discount cannot exceed due (${due})`);
            return;
        }
        if (numericAmount > 0 && !form.receiptNumber.trim()) {
            setError('Receipt number is required when recording a payment');
            return;
        }
        if (numericDiscount > 0 && !(form.discountReason || '').trim()) {
            setError('Please enter a reason for the discount');
            return;
        }
        if (!form.student || !form.course) {
            setError('Please select a student & course');
            return;
        }

        try {
            await onSubmit({
                ...form,
                amount: numericAmount,
                discountAmount: numericDiscount,
                discountReason: numericDiscount > 0 ? form.discountReason?.trim() : ''
            });
        } catch (err: any) {
            const msg =
                err.response?.data?.errors?.map((v: any) => v.msg).join(', ') ||
                err.response?.data?.message ||
                err.message;
            setError(msg);
        }
    };

    return (
        <div className={styles.overlay}>
            <form className={styles.container} onSubmit={handleSubmit}>
                <div className={styles.header}>
                    <h1 className={styles.title}>Record Payment</h1>
                    <p className={styles.subtitle}>Record cash received, a granted discount, or both</p>
                </div>

                {error && <div className={styles.error}>{error}</div>}

                <div className={styles.grid}>
                    <div className={styles.group} style={{ gridColumn: '1 / -1' }}>
                        <label className={styles.label}>Student & Course</label>
                        {initialOption ? (
                            <div className={styles.readonly}>
                                {initialOption.studentName} ({initialOption.studentEmail}) — {initialOption.courseName} | Due: {initialOption.remaining}
                            </div>
                        ) : (
                            <select
                                className={styles.input}
                                value={selectedIdx}
                                onChange={handleSelect}
                                required
                            >
                                <option value={-1}>Select student & course…</option>
                                {options.map((opt, i) => (
                                    <option key={`${opt.studentId}-${opt.courseId}`} value={i}>
                                        {opt.studentName} ({opt.studentEmail}) — {opt.courseName} | Due: {opt.remaining}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    <input type="hidden" name="student" value={form.student} />
                    <input type="hidden" name="course" value={form.course} />

                    <div className={styles.group}>
                        <label className={styles.label}>Payment Date</label>
                        <input
                            type="date"
                            name="paymentDate"
                            className={styles.input}
                            value={form.paymentDate}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className={styles.group}>
                        <label className={styles.label}>Receipt Number</label>
                        <input
                            name="receiptNumber"
                            className={styles.input}
                            value={form.receiptNumber}
                            onChange={handleChange}
                            required={Number(form.amount || 0) > 0}
                        />
                    </div>

                    <div className={styles.group}>
                        <label className={styles.label}>Payment received</label>
                        <input
                            type="text"
                            name="amount"
                            className={`${styles.input} ${styles.noSpinner}`}
                            value={form.amount}
                            onChange={handleChange}
                            placeholder="0"
                        />
                    </div>

                    <div className={styles.group}>
                        <label className={styles.label}>Discount granted</label>
                        <input
                            type="text"
                            name="discountAmount"
                            className={`${styles.input} ${styles.noSpinner}`}
                            value={form.discountAmount}
                            onChange={handleChange}
                            placeholder="0"
                        />
                    </div>

                    {Number(form.discountAmount || 0) > 0 && (
                        <div className={styles.group} style={{ gridColumn: '1 / -1' }}>
                            <label className={styles.label}>Discount reason</label>
                            <input
                                name="discountReason"
                                className={styles.input}
                                value={form.discountReason || ''}
                                onChange={handleChange}
                                placeholder="e.g. Excellence award or books not included"
                                maxLength={500}
                                required
                            />
                        </div>
                    )}

                    {(Number(form.amount || 0) > 0 || Number(form.discountAmount || 0) > 0) && (
                        <div className={styles.readonly} style={{ gridColumn: '1 / -1' }}>
                            Balance after this entry: {Math.max(0, due - Number(form.amount || 0) - Number(form.discountAmount || 0))}
                        </div>
                    )}
                </div>

                <div className={styles.buttonGroup}>
                    <button type="button" className={`${styles.button} ${styles.cancel}`} onClick={onCancel}>
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className={`${styles.button} ${styles.save}`}
                        disabled={selectedIdx < 0 && !initialOption}
                    >
                        Record Payment
                    </button>
                </div>
            </form>
        </div>
    );
}
